import {
  IMPRESION_DOCUMENTO,
  IMPRESION_FORMATO,
  ImpresionDocumentoProps,
  ImpresionFormatoProps,
} from '../interfaces/impresora.ts'

export interface ImpresionDocumentoInfo {
  value: ImpresionDocumentoProps
  label: string
  descripcion: string
  /** Documento cuya asignación usa el backend como respaldo cuando este no tiene una propia */
  respaldo?: ImpresionDocumentoProps
}

/** Documentos configurables, en el orden en que se muestran */
export const IMPRESION_DOCUMENTOS: ImpresionDocumentoInfo[] = [
  { value: IMPRESION_DOCUMENTO.FACTURA, label: 'Factura', descripcion: 'Facturas emitidas' },
  {
    value: IMPRESION_DOCUMENTO.NOTA_VENTA,
    label: 'Nota de venta',
    descripcion: 'Notas de venta sin factura',
  },
  { value: IMPRESION_DOCUMENTO.PEDIDO, label: 'Pedido', descripcion: 'Comprobante de pedido' },
  {
    value: IMPRESION_DOCUMENTO.ESTADO_CUENTA,
    label: 'Estado de cuenta',
    descripcion: 'Pre-cuenta del pedido antes de cobrar',
  },
  {
    value: IMPRESION_DOCUMENTO.COMANDA,
    label: 'Comanda (contingencia)',
    descripcion: 'Se usa cuando falla la impresora del área',
    respaldo: IMPRESION_DOCUMENTO.PEDIDO,
  },
  { value: IMPRESION_DOCUMENTO.COTIZACION, label: 'Cotización', descripcion: 'Cotizaciones a clientes' },
  { value: IMPRESION_DOCUMENTO.CAJA, label: 'Caja', descripcion: 'Apertura, arqueo y cierre de caja' },
  { value: IMPRESION_DOCUMENTO.RETIRO_CAJA, label: 'Retiro de caja', descripcion: 'Comprobante de retiro' },
  {
    value: IMPRESION_DOCUMENTO.DEST_ART,
    label: 'Destino de artículos',
    descripcion: 'Comprobante de destino de artículos',
  },
]

/**
 * Documentos que un comercio puede asignar, en el orden indicado. Sin lista, todos.
 * @example documentosAsignables([IMPRESION_DOCUMENTO.FACTURA, IMPRESION_DOCUMENTO.COTIZACION])
 */
export const documentosAsignables = (documentos?: readonly ImpresionDocumentoProps[] | null) =>
  documentos
    ? [...new Set(documentos)]
        .map((d) => IMPRESION_DOCUMENTOS.find((info) => info.value === d))
        .filter((info): info is ImpresionDocumentoInfo => Boolean(info))
    : IMPRESION_DOCUMENTOS

export const getImpresionDocumentoInfo = (documento?: ImpresionDocumentoProps | null) =>
  IMPRESION_DOCUMENTOS.find((d) => d.value === documento)

export const getImpresionDocumentoLabel = (documento?: ImpresionDocumentoProps | null) =>
  getImpresionDocumentoInfo(documento)?.label ?? documento ?? ''

export const IMPRESION_FORMATO_LABEL: Record<ImpresionFormatoProps, string> = {
  ROLLO: 'Rollo',
  PDF: 'Carta / medio oficio (PDF)',
  ROLLO2: 'Rollo 2',
  ROLLO_RESUMEN: 'Rollo resumen',
  [IMPRESION_FORMATO.RO_REDUCIDO]: 'Rollo reducido',
}

/** Formatos disponibles por documento: ROLLO2, ROLLO_RESUMEN y RO_REDUCIDO solo existen para facturas */
export const getFormatosDocumento = (documento: ImpresionDocumentoProps): ImpresionFormatoProps[] =>
  documento === IMPRESION_DOCUMENTO.FACTURA
    ? (Object.values(IMPRESION_FORMATO) as ImpresionFormatoProps[])
    : [IMPRESION_FORMATO.ROLLO, IMPRESION_FORMATO.PDF]

export const IMPRESION_COPIAS_MAX = 5

/**
 * Permiso estático para configurar impresoras desde el aviso de impresión
 * (formato SECCION:SUBSECCION:ACCION, el dominio lo agrega useSecurity). Los administradores siempre pueden.
 * Esto puede cambiar por una nueva version de restriccion de menus
 * @author isi-template
 */
export const PERMISO_CONFIGURAR_IMPRESORAS = 'LOCAL:IMPRESORAS:GESTION_DE_IMPRESORAS'
